import { Injectable } from '@angular/core';
import { Subject, BehaviorSubject, Observable } from 'rxjs';
import { HttpClient, HttpEventType } from '@angular/common/http';
import { Subscription, delay } from 'rxjs';
import { isDevMode } from '@angular/core';


// --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- ---

// DEBUG CONFIG / LOGGING
const DEBUG_HTTP = false;
const DEBUG_HTTP_PROGRESS = false;
const DEBUG_DOWNLOAD_CACHE = true;
const DEBUG_MAX_LOG_LINES = 5000;

// GALLERY SERVICE PARAMS
const GALLERY_VISIBILITY_THRESHOLD = 0.1 // Trigger when at least 10% is visible
//
const DOWNLOAD_THUMB_TIMEOUT = 23000;
const DOWNLOAD_FULL_TIMEOUT = 60000;
const MAX_DOWNLOADS_IN_QUEUE         = 5;
const MAX_DOWNLOADS_TO_ALLOW_ANOTHER = 3;

// --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- ---

export function getUrlFilename(url: string) {
    const regexpLogImgUrl = /^.*\/assets\/(img|thumb).*\//;
    return url.replace(regexpLogImgUrl, "")
}

type DownloadData = {
    id: string,
    downloader: ImageDownloader,
}

export type LogLine = {
    time: string;
    prefix: string;
    msg: string;
}

// --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- ---

@Injectable({ providedIn: 'root' })

export class GalleryStateService {

    // Instance Id
    static instanceCount : number = 0;
    private myId = 'GS' + (++GalleryStateService.instanceCount);
    private downloadNo = 0;

    ngOnDestroy() {
        // Stop gallery actions
        this.destroyDownloadTasks();
    }

    // Track if any element is currently in fullscreen mode
    private activeFullscreenIdSource = new BehaviorSubject<string | null>(null);
    activeFullscreenId$ = this.activeFullscreenIdSource.asObservable();

    // Full-screen element have to call this when entering/leaving full-screen state
    setFullscreen(id: string | null) {
        this.activeFullscreenIdSource.next(id);
    }

    // Shared IntersectionObserver factory to save resources
    createObserver(callback: (visible: boolean) => void): IntersectionObserver {
        return new IntersectionObserver((entries) => {
        entries.forEach(entry => callback(entry.isIntersecting));
        }, {
        threshold: GALLERY_VISIBILITY_THRESHOLD,
        });
    }

    // ---------------- image downloads / cache

    private downloadRemoval = new Subject<string>(); // to broadcast a removed download
    public downloadRemoval$ = this.downloadRemoval.asObservable();

    private downloadMap = new Map<string, DownloadData>;
    public downloadQueue: DownloadData[] = [];

    public addDownload(downloader: ImageDownloader): string {
        const id = `D-${++this.downloadNo}`;
        const downloadData: DownloadData = { id: id, downloader: downloader };

        if (DEBUG_DOWNLOAD_CACHE && isDevMode()) this.logMsg('GSVC', `Download: #${id} ADD TO QUEUE url=${getUrlFilename(downloader.url)}`);

        this.downloadQueue.unshift(downloadData);
        this.downloadMap.set(id, downloadData);

        if (this.downloadQueue.length > MAX_DOWNLOADS_IN_QUEUE) {
            const lastDownload = this.downloadQueue.pop();
            if (lastDownload) {
                // Inform the Observer, that this download Id is removed
                if (DEBUG_DOWNLOAD_CACHE && isDevMode()) this.logMsg('GSVC', `Download #${lastDownload.id} QUEUE LIMIT: url=${getUrlFilename(lastDownload.downloader.url)}`);
                this.downloadRemoval.next(lastDownload.id);
            }
        }
        return id;
    }

    public removeDownload(id: string) {
        this.downloadMap.delete(id);
        const count1 = this.downloadQueue.length;
        this.downloadQueue = this.downloadQueue.filter(download => download.id !== id);
        const count2 = this.downloadQueue.length;
        if (DEBUG_DOWNLOAD_CACHE && isDevMode()) this.logMsg('GSVC', `Download #${id} REMOVE: ${count1}->${count2}`);
    }

    public getActiveDownloadsNumber(): number {
        let count = 0;
        for (let downloadData of this.downloadQueue) {
            const download = downloadData.downloader;
            if (download.isLoading && download.progress < 80)
                count++;
        }
        return count;
    }
    public canStartDownload(): boolean {
        return this.getActiveDownloadsNumber() < MAX_DOWNLOADS_TO_ALLOW_ANOTHER;
    }

    destroyDownloadTasks() {
        for (let downloadData of this.downloadQueue) {
            downloadData.downloader.cancel();
        }
    }

    // ---------------- debug logger

    private debugLogs: LogLine[] = [];
    public clearDevLog() {
        this.debugLogs = [];
    }
    public getDebugLogs(): LogLine[] {
        return this.debugLogs;
    }
    public logMsg(prefix: string, msg: string) {
        const time = new Date().toLocaleTimeString('cs-CZ', { hour12: false }).split(' ')[0];       
        console.log(`[${prefix}] ${time}: ${msg}`);
        // console.log(`[${prefix}] ${time}:[${++this.logNo}] ${msg}`);
        if (!isDevMode()) {
            // Log messages are saved in Devel version only
            return;
        }
        const line: LogLine = {
            time: time,
            prefix: prefix,
            msg: msg,
        };
        this.debugLogs.unshift(line);
        if (this.debugLogs.length > DEBUG_MAX_LOG_LINES) this.debugLogs.pop();
    }

}

// --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- ---

export class ImageDownloader {
    private resultPromise?: Promise<HTMLImageElement>;
    private resolveFn?: (value: HTMLImageElement | PromiseLike<HTMLImageElement>) => void;
    private rejectFn?: (reason?: any) => void;
    public downloadId: string = '';
    public isLoading: boolean = false;
    public isLoaded: boolean = false;
    public isActive: boolean = true;
    public isVisible: boolean = true;
    public isError: boolean = false;
    public progress: number = 0;
    private blobUrl: string | undefined;

    subscription: Subscription | null = null;    

    constructor(private http: HttpClient, public readonly url: string, private useBlob: boolean) {}

    private logMsg: (prefix: string, msg: string) => void = () => {};
    public setLogMsg(fn: (prefix: string, msg: string) => void) {
        this.logMsg = fn;
    }

    public fetch(): Promise<HTMLImageElement> {

        if (this.resultPromise) {
            return this.resultPromise;
        }

        const shortName = getUrlFilename(this.url);

        if (this.useBlob) {
            this.resultPromise = new Promise<HTMLImageElement>((resolve, reject) => {
                this.resolveFn = resolve;
                this.rejectFn = reject;

                setTimeout(() => {
                    this.isLoading = true;
                }, 200);

                if (DEBUG_HTTP) this.logMsg('HTTP', `Get [${shortName}]`);

                setTimeout(() => {
                    this.isError = true;
                    if (this.subscription) {
                        this.subscription.unsubscribe();
                    }
                    reject(`Http: Timeout [${shortName}]`);
                }, DOWNLOAD_FULL_TIMEOUT);

                this.subscription = this.http.get(this.url, {
                    responseType: 'blob',
                    reportProgress: true,
                    observe: 'events'
                })
                // .pipe(delay(3000))
                .subscribe({
                    next: (event: any) => {
                        if (event.type === HttpEventType.DownloadProgress) {
                            // Percent calculation (event.total could be undefined, if server doesn't send Content-Length)
                            this.progress = event.total 
                                ? Math.round((100 * event.loaded) / event.total) 
                                : 0;
                            if (DEBUG_HTTP_PROGRESS) this.logMsg('HTTP', `[${shortName}] ${this.progress}%`);
                        } else if (event.type === HttpEventType.Response) {
                            // Download complete
                            this.isLoading = false;
                            this.isLoaded = true;
                            this.progress = 100;
                            if (event.ok) {
                                if (DEBUG_HTTP) this.logMsg('HTTP', `Download complete [${shortName}]`);
                                this.processBlob(event.body);
                            } else {
                                this.isError = true;
                                reject(`Http: Response [${shortName}] !ok status=${event.status}`);
                            }
                        }
                    },
                    error: (error) => {
                        if (DEBUG_HTTP) this.logMsg('HTTP', `[${shortName}]\nurl=${this.url} Error '${error}'`);
                        this.isLoading = false;
                        this.isError = true;
                        if (this.subscription) {
                            this.subscription.unsubscribe();
                        }
                        reject(`Http: Error [${shortName}] "${error}"`);
                    }
                });
            });
        }
        else {
            this.resultPromise = new Promise<HTMLImageElement>((resolve, reject) => {
                setTimeout(() => {
                    reject(`Img Load: Timeout ${shortName}`);
                }, DOWNLOAD_THUMB_TIMEOUT);
                const img = new Image();
                img.onload = () => {
                    // Download complete
                    this.isLoading = false;
                    resolve(img);
                };
                img.onerror = (error) => {
                    if (DEBUG_HTTP) this.logMsg('IMG', `[${shortName}]\nurl=${this.url} Error '${error}'`);
                        reject(`Img Load: Error [${shortName}] "${error}"`);
                };
                img.src = this.url;
            });
        }

        return this.resultPromise;
    }

    private processBlob(blob: Blob) {
        this.blobUrl = URL.createObjectURL(blob);
        let image = new Image();
        image.src = this.blobUrl;
        image.decode()
        .then(() => {
            if (this.resolveFn)
                this.resolveFn(image);
        })
        .catch(() => {
            if (this.rejectFn)
                this.rejectFn("Blob decoding failed");
        });
    }

    public cancel() {
        if (this.subscription) {
            this.subscription.unsubscribe();
        }
        this.subscription = null;
        if (this.blobUrl) {
            URL.revokeObjectURL(this.blobUrl);
            this.blobUrl = undefined;
        }
        this.rejectFn?.("Cancel");
    }
}
